import React, { useMemo } from 'react';
import { Divider, Flex, TextV2, Box, CloseButton, ButtonV2 } from '@withjoy/joykit';
import { LocationDatesOccupants } from '@apps/stays/AccommodationsConsole/components/LocationDatesOccupantsSearchBar/LocationDatesOccupantsSearchBar';
import type { StaysSearchTerms } from '@apps/stays/types';
import type { AccommodationsConsoleState, TopBarConfig, SearchFormConfig } from '@shared/types/Accommodations.types';
import { ArrowLeft, ChevronBack, Filter, MapMarker } from '@withjoy/joykit/icons';
import { useEventLocaleFormatter } from '@localization/hooks/useEventLocaleFormatter';
import { useTranslation } from '@shared/core';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import { getSearchFormInitialValue } from '@shared/utils/accommodations/AccommodationTiles';
import { pxToRem } from '@withjoy/joykit/theme';

import { JoyLogo } from '@assets/index';
import { TravelCurrencySelector } from '@shared/components/TravelCurrencySelector';
import { CurrencyPickerWithFundNotice } from './CurrencyPickerWithFundNotice';
import { useMediaQuery } from '@withjoy/joykit/utils';
import { ConnectedTravelProductToggle } from '@apps/stays/components/TravelProductToggle';

type AccommodationsConsoleHeaderProps = Readonly<{
  handleFormSubmit: (value: StaysSearchTerms) => void;
  state: AccommodationsConsoleState;
  onFilterPanelOpen: () => void;
  onSearchOpen: () => void;
  onClose?: () => void;
  topBar: TopBarConfig;
  /** True when the scenario presets the dates: the location, dates and guests bar is not rendered. */
  isSearchBarHidden?: boolean;
  /** True when the listings' title row draws the currency picker, so the desktop bar does not. */
  isDesktopCurrencySelectorHidden?: boolean;
  /** Optional search-CTA background (cash-fund gold). */
  searchCtaColor?: string;
  /** Cash-fund variant: the page currency is fixed to the fund's and the picker explains why. */
  cashFundNotice?: TopBarConfig['cashFundNotice'];
  /** Required to build the car-search URL for the product toggle. */
  eventHandle?: Maybe<string>;
}>;

type MobileHeaderProps = Readonly<{
  state: AccommodationsConsoleState;
  onFilterPanelOpen: () => void;
  onSearchOpen: () => void;
  onClose?: () => void;
  searchForm: SearchFormConfig;
  isSearchBarHidden?: boolean;
  hideCurrencySelector?: boolean;
  /** Cash-fund: note beside the currency picker. */
  cashFundNotice?: TopBarConfig['cashFundNotice'];
}>;

type DeskTopHeaderProps = Readonly<{
  handleFormSubmit: (value: StaysSearchTerms) => void;
  searchFormInitialValue: Partial<StaysSearchTerms>;
  onClose?: () => void;
  topBar: TopBarConfig;
  isSearchBarHidden?: boolean;
  isCurrencySelectorHidden?: boolean;
  /** Optional search-CTA background (cash-fund gold). */
  searchCtaColor?: string;
  /** Cash-fund variant: the page currency is fixed to the fund's and the picker explains why. */
  cashFundNotice?: TopBarConfig['cashFundNotice'];
  productToggleEventHandle?: Maybe<string>;
}>;

const Container = ({ children }: { children?: React.ReactNode }) => (
  <Flex
    alignItems="center"
    gap={4}
    padding={4}
    borderBottom="1px solid"
    background="white"
    position="sticky"
    top={0}
    zIndex="dropdown"
    borderColor="mono3"
  >
    {children}
  </Flex>
);

const AccommodationsConsoleHeaderInner = ({
  state,
  topBar,
  isSearchBarHidden,
  isDesktopCurrencySelectorHidden,
  onClose,
  eventHandle,
  onSearchOpen,
  searchCtaColor,
  handleFormSubmit,
  onFilterPanelOpen,
}: AccommodationsConsoleHeaderProps) => {
  const isMobileOrTablet = useIsMobileOrTablet();
  const searchFormInitialValue = useMemo(() => {
    return getSearchFormInitialValue(state);
  }, [state]);

  const productToggleEventHandle = topBar.showTravelProductToggle ? eventHandle : undefined;
  const mobileProductToggleEventHandle = isMobileOrTablet ? productToggleEventHandle : undefined;
  // The cash-fund notice opens its own tooltip on load; two open at once crowd the header. The
  // honeymoon pages opt out outright (see TopBarConfig.hideCarsPromoTooltip).
  const showCarsPromoTooltip = !topBar.cashFundNotice && !topBar.hideCarsPromoTooltip;

  return (
    <>
      <Flex
        flexDirection="column"
        borderBottom="1px solid"
        background={topBar.backgroundColor ?? 'white'}
        position="sticky"
        top={0}
        zIndex="dropdown"
        borderColor="mono3"
      >
        {mobileProductToggleEventHandle && (
          <ConnectedTravelProductToggle
            showCarsPromoTooltip={showCarsPromoTooltip}
            activeProduct="hotels"
            eventHandle={mobileProductToggleEventHandle}
          />
        )}
        <Flex
          gap={4}
          padding={4}
          alignItems="center"
          paddingX={topBar.horizontalPadding}
        >
          {isMobileOrTablet ? (
            <MobileHeader
              state={state}
              onClose={onClose}
              onSearchOpen={onSearchOpen}
              searchForm={topBar.searchForm}
              isSearchBarHidden={isSearchBarHidden}
              onFilterPanelOpen={onFilterPanelOpen}
              hideCurrencySelector={topBar.hideCurrencySelectorOnMobile}
              cashFundNotice={topBar.cashFundNotice}
            />
          ) : (
            <DeskTopHeader
              topBar={topBar}
              isSearchBarHidden={isSearchBarHidden}
              isCurrencySelectorHidden={isDesktopCurrencySelectorHidden}
              onClose={onClose}
              searchCtaColor={searchCtaColor}
              handleFormSubmit={handleFormSubmit}
              searchFormInitialValue={searchFormInitialValue}
              productToggleEventHandle={productToggleEventHandle}
              cashFundNotice={topBar.cashFundNotice}
            />
          )}
        </Flex>
      </Flex>
    </>
  );
};

const MobileHeader = ({ state, onFilterPanelOpen, onSearchOpen, onClose, searchForm, isSearchBarHidden, hideCurrencySelector, cashFundNotice }: MobileHeaderProps) => {
  const { formatDate } = useEventLocaleFormatter();
  const checkinDate = formatDate(state.searchDates.checkin, 'MMMdd') ?? '';
  const checkoutDate = formatDate(state.searchDates.checkout, 'dd') ?? '';
  const adults = state.occupancies.occupancies?.reduce((acc, occ) => acc + occ.adults, 0) || 0;
  const children = state.occupancies.occupancies?.reduce((acc, occ) => acc + occ.childrenAges.length, 0) || 0;
  const { t } = useTranslation('accommodationsConsole');
  const headerTrans = t('header');
  const showDatesOrOccupancies = searchForm.dateRangeEnabled || searchForm.occupancySelectorEnabled;

  return (
    <Flex
      gap={4}
      width={'100%'}
      justifyContent={'space-between'}
      alignItems={'center'}
    >
      {onClose && (
        <ArrowLeft
          size={22}
          cursor={'pointer'}
          onClick={onClose}
        />
      )}
      {isSearchBarHidden ? (
        <Box flex={1} />
      ) : (
        <Flex
          flex={1}
          minWidth={0}
          overflow="hidden"
          onClick={onSearchOpen}
          cursor={'pointer'}
          alignItems={'center'}
          paddingX={7}
          paddingY={3}
          flexDirection={'column'}
          border={'1px solid'}
          borderColor={'mono3'}
          borderRadius="full"
          // Keep the field white so it doesn't inherit a themed header background (e.g. the cash-fund
          // cream). No-op for guest/admin/ota — their header is already white.
          backgroundColor="white"
        >
          <Flex
            alignItems={'center'}
            gap={2}
          >
            {!showDatesOrOccupancies && <MapMarker size={18} />}
            <TextV2
              typographyVariant={'label2'}
              fontWeight={400}
              maxWidth={pxToRem(150)}
              whiteSpace="nowrap"
              overflow="hidden"
              textOverflow="ellipsis"
            >
              {state.searchLocation}
            </TextV2>
          </Flex>
          {showDatesOrOccupancies && (
            <Flex
              alignItems={'center'}
              marginTop={2}
              marginRight={4}
              gap={4}
            >
              {searchForm.dateRangeEnabled && (
                <>
                  <TextV2
                    typographyVariant={'label1'}
                    color={'mono10'}
                  >
                    {checkinDate} — {checkoutDate}
                  </TextV2>
                  {searchForm.occupancySelectorEnabled && (
                    <Divider
                      orientation="vertical"
                      height={14}
                    />
                  )}
                </>
              )}
              {searchForm.occupancySelectorEnabled && (
                <TextV2
                  typographyVariant={'label1'}
                  color={'mono10'}
                >
                  {headerTrans.rooms.rooms({ rooms: state.occupancies.unitCount, count: state.occupancies.unitCount })},{' '}
                  {headerTrans.guests.guests({ guests: adults + children, count: adults + children })}
                </TextV2>
              )}
            </Flex>
          )}
        </Flex>
      )}
      <Flex
        alignItems="center"
        gap={6}
      >
        <Filter
          size={22}
          cursor={'pointer'}
          onClick={onFilterPanelOpen}
        />
        {!hideCurrencySelector && <Box marginTop={1}>{cashFundNotice ? <CurrencyPickerWithFundNotice notice={cashFundNotice} /> : <TravelCurrencySelector isEnabled />}</Box>}
      </Flex>
    </Flex>
  );
};

const DeskTopHeader = ({
  handleFormSubmit,
  searchFormInitialValue,
  onClose,
  topBar,
  isSearchBarHidden,
  isCurrencySelectorHidden,
  searchCtaColor,
  productToggleEventHandle,
  cashFundNotice,
}: DeskTopHeaderProps) => {
  const { backButton, showCloseButton, showJoyLogo, searchForm } = topBar;
  const { tq } = useTranslation('accommodationsConsole');

  return (
    <Box flex={1}>
      {showJoyLogo && (
        <Flex
          position="absolute"
          left={pxToRem(24)}
          top={pxToRem(24)}
          alignItems="center"
        >
          <JoyLogo
            height={pxToRem(30)}
            width={pxToRem(47)}
          />
        </Flex>
      )}
      {backButton && (
        <BackButton
          label={backButton.label}
          ariaLabel={backButton.ariaLabel}
          onClose={onClose}
        />
      )}
      <Flex
        gap={pxToRem(24)}
        alignItems="center"
        justifyContent="center"
      >
        {productToggleEventHandle && (
          <ConnectedTravelProductToggle
            showCarsPromoTooltip={!cashFundNotice && !topBar.hideCarsPromoTooltip}
            activeProduct="hotels"
            eventHandle={productToggleEventHandle}
          />
        )}
        {!isSearchBarHidden && (
          <LocationDatesOccupants
            config={{
              ...searchForm,
              isOpenedFormView: false,
            }}
            onSubmit={handleFormSubmit}
            accentColor={searchCtaColor}
            initialValue={searchFormInitialValue}
          />
        )}
      </Flex>
      {!isCurrencySelectorHidden && (
        <Flex
          top={pxToRem(24)}
          alignItems="center"
          position="absolute"
          right={pxToRem(45)}
        >
          {cashFundNotice ? <CurrencyPickerWithFundNotice notice={cashFundNotice} /> : <TravelCurrencySelector isEnabled />}
        </Flex>
      )}
      {showCloseButton && (
        <CloseButton
          shape="rounded"
          size="lg"
          position="absolute"
          top="1.25rem"
          right="1.25rem"
          onClick={onClose}
          aria-label={tq`Close`}
        />
      )}
    </Box>
  );
};

interface BackButtonProps {
  label: Maybe<string>;
  ariaLabel?: string;
  onClose?: () => void;
}

const BackButton: React.FC<BackButtonProps> = ({ label, ariaLabel, onClose }) => {
  const showLabel = useMediaQuery(theme => theme.mediaQueries.up({ breakpointAlias: 'md3' }));

  return (
    <Flex
      position={'absolute'}
      left={pxToRem(24)}
      top={pxToRem(24)}
      alignItems={'center'}
    >
      <ButtonV2
        variant="outline"
        border={'1px solid'}
        borderColor={'mono3'}
        height={8}
        minWidth={'fit-content'}
        minHeight={'fit-content'}
        padding={pxToRem(7)}
        intent="neutral"
        marginRight={showLabel ? 4 : 0}
        width={8}
        data-testid="back-button"
        aria-label={ariaLabel ?? label ?? undefined}
        shape="rounded"
        startIcon={<ChevronBack size={16} />}
        onClick={onClose}
      />
      {label && showLabel && (
        <TextV2
          typographyVariant={'hed4'}
          whiteSpace="nowrap"
        >
          {label}
        </TextV2>
      )}
    </Flex>
  );
};

const AccommodationsConsoleHeader = Object.assign(React.memo(AccommodationsConsoleHeaderInner), {
  Skeleton: () => <Container />,
});

export { AccommodationsConsoleHeader };
